import {
  Badge,
  Button,
  Card,
  Form,
  Grid,
  Popover,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import Container from '../../../components/Container';
import FiveHundred from '../../../components/FiveHundred';
import FourZeroThree from '../../../components/FourZeroThree';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import {
  DEFAULT_FONT_SIZE,
  DEFAULT_SIZE_TYPE,
  GENERAL_TIMEOUT,
} from '../../../constants/systemConstants';
import { useVT } from 'virtualizedtableforantd4';
import { FontSizeType, MailTempData, MailTempEnum } from '../../../types';
import { useTranslation } from 'react-i18next';
import Table, { ColumnsType } from 'antd/lib/table';
import { compare, setFont } from '../../../utils/colComponents';
import { actionPermissions } from '../../../constants/actionPermissions';
import { hasPermission } from '../../../utils/hasPermission';
import {
  CheckOutlined,
  CloseOutlined,
  FilterOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { GREEN1, RED1 } from '../../../constants/color';
import Popconfirm from 'antd/es/popconfirm';
import TableToolbar from '../../../components/table/TableToolbar';
import { alertMessage } from '../../../utils/alertMessage';
import {
  getDataWithAuthToken,
  postDataWithAuthToken,
} from '../../../utils/axiosRequest';
import { tableScrollToTop } from '../../../utils/helperFunction';
import TableFooterToolbar from '../../../components/table/TableFooterToolbar';
import MailTemplateModal from '../../../components/plugins/MailTemplateModal';

const MailTempList = () => {
  //General Component
  const formRef = useRef(null);
  const isSubscribed = useRef(true);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [fourZeroThree, setFourZeroThree] = useState(false);
  const [fiveHundred, setFiveHundred] = useState(false);
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const [advance, setAdvance] = useState<{
    [key: string]: any;
  }>({});
  //Table Component
  const [tableSize, setTableSize] = useState<SizeType>(DEFAULT_SIZE_TYPE);
  const [total, setTotal] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [vt] = useVT(() => ({ scroll: { y: 600 } }), []);
  const [form] = Form.useForm();
  const columnKeys = [
    'templateId',
    'templateCode',
    'templateSubject',
    'templateContent',
    'templateType',
    'isHtml',
    'updateTime',
    'action',
  ];
  const [selectedColumns, setSelectedColumns] = useState(columnKeys);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  //Data Component
  const [mailTempData, setMailTempData] = useState<MailTempData[]>([]);
  const [selectedMailTemp, setSelectedMailTemp] = useState<MailTempData>();
  const [MailTempEnums, setMailTempEnums] = useState<MailTempEnum>();
  //Text Component
  const [fontSize, setFontSize] = useState<FontSizeType>(DEFAULT_FONT_SIZE);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout>();
  const { t } = useTranslation();

  const columns: ColumnsType<MailTempData> = [
    {
      title: setFont(t('plugins.Mail.mailTemp.templateId'), fontSize),
      key: 'templateId',
      dataIndex: 'templateId',
      fixed: screens.lg ? 'left' : undefined,
      width: 80,
      render: (text: string, record: MailTempData) => (
        <Button
          type="link"
          disabled={!hasPermission(actionPermissions.pluginGroup.mailTemp)}
          onClick={() => {
            setSelectedMailTemp(record);
            setShowModal(true);
          }}
          style={{ padding: 0, fontSize: fontSize }}
        >
          {text}
        </Button>
      ),
      sorter: (a: MailTempData, b: MailTempData) =>
        compare(a.templateId, b.templateId),
    },
    {
      title: setFont(t('plugins.Mail.mailTemp.templateCode'), fontSize),
      key: 'templateCode',
      dataIndex: 'templateCode',
      width: 100,
      render: (text: string) => setFont(text, fontSize),
    },
    {
      title: setFont(t('plugins.Mail.mailTemp.templateType'), fontSize),
      key: 'templateType',
      dataIndex: 'templateType',
      width: 100,
      render: (text: string, record: MailTempData) => (
        <Tag color={record.templateType.tagColor}>
          {record.templateType.description}
        </Tag>
      ),
    },
    {
      title: setFont(t('plugins.Mail.mailTemp.templateSubject'), fontSize),
      key: 'templateSubject',
      dataIndex: 'templateSubject',
      width: 150,
      render: (text: string) => setFont(text, fontSize),
    },
    {
      title: setFont(t('plugins.Mail.mailTemp.templateContent'), fontSize),
      key: 'templateContent',
      dataIndex: 'templateContent',
      width: 300,
      // ellipsis: true,
      render: (text: string) => (
        <Typography.Text ellipsis={{ tooltip: text }}>{text}</Typography.Text>
      ),
    },
    {
      title: setFont(t('plugins.Mail.mailTemp.isHtml'), fontSize),
      key: 'isHtml',
      dataIndex: 'isHtml',
      width: 80,
      render: (value: boolean) =>
        value ? (
          <CheckOutlined style={{ color: GREEN1, fontSize: fontSize }} />
        ) : (
          <CloseOutlined style={{ color: RED1, fontSize: fontSize }} />
        ),
    },
    {
      title: setFont(t('plugins.Mail.mailTemp.updateTime'), fontSize),
      key: 'updateTime',
      dataIndex: 'updateTime',
      width: 100,
      render: (text: string) => setFont(text, fontSize),
    },

    {
      title: setFont(t('actionsColumn.title'), fontSize),
      width: 80,
      key: 'action',
      fixed: screens.lg ? 'right' : undefined,
      render: (record: MailTempData) => (
        <Space>
          <Button
            disabled={!hasPermission(actionPermissions.pluginGroup.mailTemp)}
            type="link"
            style={{ padding: 0, fontSize: fontSize }}
            onClick={() => {
              if (MailTempEnums && MailTempEnums.tempCodeList.length > 0) {
                setSelectedMailTemp(record);
                setShowModal(true);
              }
            }}
          >
            {t('actionsColumn.edit')}
          </Button>
          {hasPermission(actionPermissions.pluginGroup.mailTemp) && (
            <Popconfirm
              title={setFont(t('actionsColumn.deleteWarning'), fontSize)}
              onConfirm={() => {
                deleteMailTemp(record.templateId);
              }}
              okText={t('actionsColumn.confirmation.yes')}
              cancelText={t('actionsColumn.confirmation.no')}
              placement="leftBottom"
            >
              <Button type="link" danger style={{ fontSize: fontSize }}> {t('actionsColumn.delete')}</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const deleteMailTemp = (templateId: number) => {
    if (isSubscribed.current) setIsLoading(true);
    postDataWithAuthToken('mail_temp/delete', { templateId: templateId })
      .then((response) => {
        if (response && response.goodStatus) {
          alertMessage(
            'success',
            t('general.alerts.deleted', {
              name: t('plugins.Mail.mailTemp.title'),
            })
          );
          getMailTempData();
        } else {
          alertMessage(
            'error',
            response?.msg || t('general.noResponse'),
            response?.data || undefined
          );
        }
        if (isSubscribed.current) setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        if (isSubscribed.current) setIsLoading(false);
      });
  };

  const getMailTempData = useCallback(() => {
    if (isSubscribed.current) setIsLoading(true);
    getDataWithAuthToken('mail_temp/list', {
      params: {
        keyword: keyword || undefined,
        templateCode:
          formRef.current && form.getFieldValue('templateCode')
            ? form.getFieldValue('templateCode') === 'all'
              ? undefined
              : form.getFieldValue('templateCode')
            : undefined,
        templateType:
          formRef.current && form.getFieldValue('templateType')
            ? form.getFieldValue('templateType') === 'all'
              ? undefined
              : form.getFieldValue('templateType')
            : undefined,
        isHtml:
          formRef.current && form.getFieldValue('isHtml')
            ? form.getFieldValue('isHtml') === 'all'
              ? undefined
              : form.getFieldValue('isHtml')
            : undefined,
      },
    })
      .then((response) => {
        if (response) {
          if (response.goodStatus) {
            if (isSubscribed.current) {
              setMailTempData(response.data.list);
              setTotal(response.data.totalItem);
              setSelectedRowKeys([]);
              // Scroll to top when data changes
              tableScrollToTop();
            }
          } else if (response.returnCode === 403) {
            if (isSubscribed.current) setFourZeroThree(true);
          } else {
            isSubscribed.current && setFiveHundred(true);
            alertMessage(
              'error',
              response?.msg || t('general.noResponse'),
              response?.data || undefined
            );
          }
        } else isSubscribed.current && setFiveHundred(true);
        if (isSubscribed.current) setIsLoading(false);
      })
      .catch((err) => {
        if (isSubscribed.current) setIsLoading(false);
        console.log(err);
      });
  }, [t, form, keyword]);

  const getMailTempEnums = useCallback(() => {
    getDataWithAuthToken('mail_temp/enum_list')
      .then((response) => {
        if (response && response.goodStatus) {
          if (isSubscribed.current) setMailTempEnums(response.data);
        } else {
          alertMessage(
            'error',
            response?.msg || t('general.noResponse'),
            response?.data || undefined
          );
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, [t]);

  // Sets isSubscribed to false if component becomes unmounted
  useEffect(() => {
    return () => {
      isSubscribed.current = false;
    };
  }, []);

  useEffect(() => {
    getMailTempData();
  }, [getMailTempData]);

  useEffect(() => {
    getMailTempEnums();
  }, [getMailTempEnums]);

  const advancedSearch = (
    <Popover
      overlayStyle={{ zIndex: 100 }}
      style={{ width: '200%' }}
      title={t('general.advancedSearch')}
      trigger="click"
      placement="bottomRight"
      content={
        <Form
          layout="vertical"
          form={form}
          ref={formRef}
          initialValues={{ templateCode: 'all' }}
        >
          <Form.Item
            label={t('plugins.Mail.mailTemp.templateCode')}
            name="templateCode"
          >
            <Select
              placeholder={t('general.inputError.pleaseSelectOne')}
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              filterOption={false}
              onChange={(value) =>
                setAdvance((prev: any) => ({
                  ...prev,
                  templateCode: value === 'all' ? '' : value,
                }))
              }
            >
              <Select.Option key="all" value="all">
                {t('general.all')}
              </Select.Option>
              {MailTempEnums?.tempCodeList.map((tempCode) => (
                <Select.Option key={tempCode.code} value={tempCode.code}>
                  {tempCode.code}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label={t('plugins.Mail.mailTemp.templateType')}
            name="templateType"
          >
            <Select
              placeholder={t('general.inputError.pleaseSelectOne')}
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              filterOption={false}
              onChange={(value) =>
                setAdvance((prev: any) => ({
                  ...prev,
                  templateType: value === 'all' ? '' : value,
                }))
              }
            >
              <Select.Option key="all" value="all">
                {t('general.all')}
              </Select.Option>
              {MailTempEnums?.tempTypeList.map((tempType) => (
                <Select.Option key={tempType.code} value={tempType.code}>
                  {tempType.description}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="isHtml" label={t('plugins.Mail.mailTemp.isHtml')}>
            <Select
              style={{ width: 140 }}
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              filterOption={false}
              optionLabelProp="key"
              onChange={(value) => {
                setAdvance((prev: any) => ({
                  ...prev,
                  isHtml: value === 'all' ? '' : value.toString(),
                }));
              }}
            >
              <Select.Option key={`${t('general.all')}`} value="all">
                {t('general.all')}
              </Select.Option>
              <Select.Option
                key={`${t('general.booleanToStatus.true')}`}
                value={true}
              >
                {t('general.booleanToStatus.true')}
              </Select.Option>
              <Select.Option
                key={`${t('general.booleanToStatus.false')}`}
                value={false}
              >
                {t('general.booleanToStatus.false')}
              </Select.Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                htmlType="submit"
                type="primary"
                onClick={() => {
                  setKeyword('');
                  if (typingTimeout) clearTimeout(typingTimeout);
                  setTypingTimeout(
                    setTimeout(() => getMailTempData(), GENERAL_TIMEOUT)
                  );
                }}
              >
                {t('general.search')}
              </Button>
              <Button
                disabled={Object.values(advance).every((value) => !value)}
                onClick={() => {
                  form.resetFields();
                  setAdvance({});
                  getMailTempData();
                }}
              >
                {t('general.reset')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      }
    >
      <Badge
        count={Object.keys(advance).reduce((accumulator, obj) => {
          if (advance[obj as keyof typeof advance]) {
            return accumulator + 1;
          }

          return accumulator;
        }, 0)}
      >
        <Button icon={<FilterOutlined />}>{t('general.advancedSearch')}</Button>
      </Badge>
    </Popover>
  );

  return (
    <Container>
      {fourZeroThree ? (
        <Card>
          <FourZeroThree />
        </Card>
      ) : fiveHundred ? (
        <Card>
          <FiveHundred />
        </Card>
      ) : (
        <>
          <Card>
            <Typography.Title level={3} style={{ fontWeight: 500 }}>
              {t('plugins.Mail.mailTemp.title')}
            </Typography.Title>
            <TableToolbar
              advancedSearch={advancedSearch}
              setFontSize={setFontSize}
              fontSize={fontSize}
              leftElement={
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setSelectedMailTemp(undefined);
                    setShowModal(true);
                  }}
                  disabled={
                    !hasPermission(actionPermissions.pluginGroup.mailTemp)
                  }
                >
                  {t('plugins.Mail.mailTemp.addTitle')}
                </Button>
              }
              tableSize={tableSize}
              setTableSize={setTableSize}
              refresh={() => {
                getMailTempData();
              }}
              totalItems={total}
              columns={columns}
              columnKeys={columnKeys}
              selectedColumns={selectedColumns}
              setSelectedColumns={setSelectedColumns}
              search={(keyword) => {
                setKeyword(keyword);
              }}
              searchPlaceholder={t('searchPlaceholders.searchMailTemp')}
              rows={mailTempData.map((mailTemp) => ({
                ...mailTemp,
              }))}
              exportConfig={{
                fileName: 'MAIL_TEMPLATES',
              }}
            />

            <Table<MailTempData>
              dataSource={mailTempData}
              rowKey={(temp) => temp.templateId}
              columns={columns.filter((x) =>
                selectedColumns.includes(x.key?.toString() ?? '')
              )}
              components={vt}
              scroll={{ y: 600, x: 1200 }}
              size={tableSize}
              loading={isLoading}
              pagination={{
                total: total,
                showQuickJumper: true,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  t('general.paginationTotal', {
                    start: range[0],
                    end: range[1],
                    total: total,
                  }),
                size: 'small',
              }}
            />

            {!!selectedRowKeys.length && (
              <TableFooterToolbar
                selectedRows={[]}
                columns={columns.filter((x) =>
                  selectedColumns.includes(x.key?.toString() ?? '')
                )}
                funct={{ exportConfig: { fileName: 'MAIL_TEMPLATES' } }}
              />
            )}
          </Card>

          <MailTemplateModal
            visible={showModal}
            setVisible={setShowModal}
            callBack={() => getMailTempData()}
            mailTempEnums={MailTempEnums}
            selectedMailTemp={selectedMailTemp}
          />
        </>
      )}
    </Container>
  );
};

export default MailTempList;
